import { EvaluatedValue, FileKind, Input, NodeId } from 'web';
import { getAxiosInstance } from '@/services/axiosConfig';
import { ExportData } from '@/types/node';
import { HandleType } from '@/enums/handle-type.enum';
import type { FlowGraph } from '../FlowGraph';

const EXPORTABLE_TYPES = [HandleType.Image, HandleType.Mask, HandleType.Video, HandleType.Audio, HandleType.ThreeDee];

const axiosInstance = getAxiosInstance();

type FileInfo = { url?: string; blob?: Blob; type: FileKind['type'] };
type FileResult = { fileInfo: FileInfo | null; error: string | null };

export class ExportView {
  constructor(
    private graph: FlowGraph,
    private id: NodeId,
  ) {}

  isExportable(): boolean {
    const inputNode = this.graph.store.getNodeDataNullable<ExportData>(this.id)?.inputNode;
    if (!inputNode || !this.graph.store.getNodeNullable(inputNode.nodeId)) {
      return false;
    }
    const outputType = this.graph.nodeOutputType(inputNode.nodeId, inputNode.outputId);
    return outputType !== undefined && EXPORTABLE_TYPES.includes(outputType);
  }

  private validateAndGetEvaluatedFile():
    | { success: true; evaluatedFile: EvaluatedValue; inputNode: Input }
    | { success: false; error: string } {
    if (!this.isExportable()) {
      return { success: false, error: 'Node is not exportable' };
    }

    const inputNode = this.graph.store.getNodeDataNullable<ExportData>(this.id)?.inputNode;
    if (!inputNode) {
      return { success: false, error: 'Input not found' };
    }

    const evaluatedFile = this.graph.evaluateOutput(inputNode);
    if (!evaluatedFile) {
      return { success: false, error: 'Evaluated file not found' };
    }

    return { success: true, evaluatedFile, inputNode };
  }

  private resolveImageFile(evaluatedFile: EvaluatedValue, inputNode: Input): FileResult {
    // Handle computed images by rendering them
    if (
      typeof evaluatedFile.value === 'object' &&
      'type' in evaluatedFile.value &&
      evaluatedFile.value.type === 'computed'
    ) {
      const computedImage = this.graph.wasm.call((wasm) => wasm.exportNodeRender(inputNode.nodeId, inputNode.outputId));
      if (!computedImage) {
        return { fileInfo: null, error: 'Computed image not found' };
      }
      return { fileInfo: { url: computedImage.data, type: 'image' }, error: null };
    }

    // Handle non-computed images
    if (typeof evaluatedFile.value === 'object' && 'url' in evaluatedFile.value) {
      return { fileInfo: { url: evaluatedFile.value.url, type: 'image' }, error: null };
    }

    return { fileInfo: null, error: 'Invalid image data' };
  }

  private async resolveVideoAudioFile(
    evaluatedFile: EvaluatedValue,
    inputNode: Input,
    recipeId: string,
    saveRecipe: () => Promise<void>,
  ): Promise<FileResult> {
    const fileType = evaluatedFile.type as FileKind['type'];
    // Handle computed videos/audio - return blob directly
    if (
      typeof evaluatedFile.value === 'object' &&
      'type' in evaluatedFile.value &&
      evaluatedFile.value.type === 'computed'
    ) {
      const nodeId = inputNode.nodeId;
      const outputId = inputNode.outputId;
      await saveRecipe();
      const res = await axiosInstance.get<Blob>(`/v1/recipes/${recipeId}/nodes/${nodeId}/${outputId}/download-video`, {
        responseType: 'blob',
        'axios-retry': { retries: 0 },
      });
      if (!res.data) {
        return { fileInfo: null, error: 'File not found' };
      }
      // Return blob directly - let the download utility handle blob URL lifecycle
      return {
        fileInfo: { blob: res.data, type: fileType },
        error: null,
      };
    }

    // Handle non-computed videos/audio
    if (typeof evaluatedFile.value === 'object' && 'url' in evaluatedFile.value) {
      return { fileInfo: { url: evaluatedFile.value.url, type: fileType }, error: null };
    }

    return { fileInfo: null, error: 'Invalid file data' };
  }

  private resolveSimpleAssetFile(evaluatedFile: EvaluatedValue): FileResult {
    const fileType = evaluatedFile.type as FileKind['type'];
    if (typeof evaluatedFile.value === 'object' && 'url' in evaluatedFile.value) {
      return { fileInfo: { url: evaluatedFile.value.url, type: fileType }, error: null };
    }
    return { fileInfo: null, error: 'Invalid asset data' };
  }

  /**
   * Resolves and prepares a file for download. For computed files, this may involve rendering (images)
   * or downloading from the server (videos). Returns the file URL and type, or an error if unsuccessful.
   */
  async resolveDownloadableFile(recipeId: string, saveRecipe: () => Promise<void>): Promise<FileResult> {
    const validation = this.validateAndGetEvaluatedFile();
    if (!validation.success) {
      return { fileInfo: null, error: validation.error };
    }

    const { evaluatedFile, inputNode } = validation;
    const fileType = evaluatedFile.type;
    switch (fileType) {
      case 'image':
        return this.resolveImageFile(evaluatedFile, inputNode);
      case 'video':
      case 'audio':
        return await this.resolveVideoAudioFile(evaluatedFile, inputNode, recipeId, saveRecipe);
      case '3D':
        return this.resolveSimpleAssetFile(evaluatedFile);
      default:
        return { fileInfo: null, error: 'File type not supported' };
    }
  }
}
